import { useCallback, useEffect, useMemo, useState } from 'react';
import { Collapse, Result } from 'antd';
import { useTranslation } from 'react-i18next';

import { Card, CardBody } from 'components/common';
import { CreateCompensationModal } from 'components/CreateCompensationModal';
import { fetchCurrentYearUserCompensations, fetchArchivedUserCompensations } from 'services';
import { useAppDispatch, useAppSelector, useModal } from 'hooks';
import { Compensation } from 'types';

import { CompensationTable, HomeHeader } from './components';
import {
  getUser,
  getCurrentYearCompensations,
  getCurrentYearIsLoading,
  getArchiveOpened,
  setArchiveOpened,
} from '../../store';
import { LegalEntity, Namespace } from '../../enums';
import styles from './MyCompensationsPage.module.scss';

export const MyCompensationsPage = () => {
  const { t } = useTranslation(Namespace.PersonalArea);
  const dispatch = useAppDispatch();
  const { isOpenModal, openModal, closeModal } = useModal();
  const user = useAppSelector(getUser);
  const currentYearCompensations = useAppSelector(getCurrentYearCompensations);
  const isCurrentYearLoading = useAppSelector(getCurrentYearIsLoading);

  const [archivedCompensations, setArchivedCompensations] = useState<Compensation[]>([]);
  const [isArchivedLoading, setIsArchivedLoading] = useState(false);
  const [isArchivedLoaded, setIsArchivedLoaded] = useState(false);
  const archiveOpened = useAppSelector(getArchiveOpened);

  useEffect(() => {
    dispatch(fetchCurrentYearUserCompensations());
  }, [dispatch]);

  useEffect(() => {
    if (!archiveOpened || isArchivedLoaded) return;

    setIsArchivedLoading(true);
    dispatch(fetchArchivedUserCompensations())
      .unwrap()
      .then((data) => {
        setArchivedCompensations(data);
        setIsArchivedLoaded(true);
      })
      .finally(() => setIsArchivedLoading(false));
  }, [archiveOpened, isArchivedLoaded, dispatch]);

  const handleSuccessSubmit = useCallback(() => {
    closeModal();
    dispatch(fetchCurrentYearUserCompensations());

    if (archiveOpened) {
      setIsArchivedLoading(true);
      dispatch(fetchArchivedUserCompensations())
        .unwrap()
        .then(setArchivedCompensations)
        .finally(() => setIsArchivedLoading(false));
    }
  }, [dispatch, closeModal, archiveOpened]);

  const hasUserAbilityToCreateOwnCompensation = useMemo(
    () => Object.values(LegalEntity).includes(user?.legalEntity as LegalEntity),
    [user?.legalEntity],
  );

  const collapseItems = useMemo(
    () => [
      {
        key: 'archived',
        label: t('otherRequests'),
        children: (
          <CompensationTable compensations={archivedCompensations} isLoading={isArchivedLoading} disableUrlParams />
        ),
      },
    ],
    [archivedCompensations, isArchivedLoading, t],
  );

  return hasUserAbilityToCreateOwnCompensation ? (
    <>
      <Card>
        <HomeHeader createRequest={openModal} />
        <CardBody>
          <CompensationTable compensations={currentYearCompensations} isLoading={isCurrentYearLoading} />
        </CardBody>
        <CreateCompensationModal
          isUserMode
          isOpen={isOpenModal}
          close={closeModal}
          onSuccessSubmit={handleSuccessSubmit}
        />
      </Card>
      <Collapse
        className={styles.othersCollapse}
        items={collapseItems}
        activeKey={archiveOpened ? ['archived'] : []}
        onChange={(keys) => {
          const isArchivedOpen = Array.isArray(keys) ? keys.includes('archived') : keys === 'archived';

          dispatch(setArchiveOpened(isArchivedOpen));
        }}
      />
    </>
  ) : (
    <Card>
      <Result className={styles.warning} status="403" subTitle={t('accessDenied')} />
    </Card>
  );
};
